import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { useAuthModal } from '../context/AuthModalContext';

const ROTATING_PHRASES = [
  'learn Salesforce development',
  'hire Salesforce developers',
  'upskill your Salesforce team',
  'ace your next Salesforce interview',
  'practice Apex and LWC daily',
];

const FEATURES = [
  { title: 'Validates against your real Salesforce org',  desc: 'Not a simulator — your code runs in your actual org.'   },
  { title: 'Explains the concept before you code',        desc: 'Every challenge teaches the why, not just the what.'    },
  { title: 'Real scenarios from real projects',           desc: 'Challenges your team faces daily, not textbook theory.' },
  { title: 'Covers all core Salesforce dev topics',       desc: 'Apex, SOQL, Triggers, Async, Integrations and more.'   },
];

const CATEGORIES = [
  { id: 'apex',         icon: '⚙️', label: 'Apex',         desc: 'Classes, Triggers, Async, DML',    diffs: ['Easy','Medium','Hard'] },
  { id: 'soql',         icon: '🔍', label: 'SOQL',         desc: 'Queries, Aggregates, SOSL',         diffs: ['Hard'] },
  { id: 'lwc',          icon: '🖥️', label: 'LWC',          desc: 'Components, Events, Wire',          diffs: ['Easy'] },
  { id: 'integrations', icon: '🔗', label: 'Integrations', desc: 'REST, SOAP, Platform Events',       diffs: ['Easy','Medium','Hard'] },
  { id: 'formula',      icon: '📐', label: 'Formulas',     desc: 'Formula fields, validation',        diffs: ['Easy','Medium'] },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Connect your org',  desc: 'Link your Salesforce dev org or sandbox with one click.' },
  { step: '02', title: 'Pick a challenge',  desc: 'Choose from Apex, SOQL, Triggers and more — easy to hard.' },
  { step: '03', title: 'Read the concept',  desc: 'Understand the pattern before writing any code.' },
  { step: '04', title: 'Validate and grow', desc: 'Your code runs in your real org. See exactly what passed.' },
];

const ACTIVITY_FEED = [
  { name: 'Priya',   action: 'solved',  item: 'Batch Apex With Callout',    mins: 2  },
  { name: 'Marcus',  action: 'solved',  item: 'PATCH Update Callout',        mins: 5  },
  { name: 'Sam',     action: 'solved',  item: 'Circuit Breaker',             mins: 11 },
  { name: 'Aisha',   action: 'joined',  item: 'Apex30',                      mins: 14, apex30: true },
  { name: 'James',   action: 'solved',  item: 'Lead Score Recalculator',     mins: 18 },
  { name: 'Neha',    action: 'solved',  item: 'Paginated API Harvester',     mins: 22 },
  { name: 'Carlos',  action: 'joined',  item: 'Apex30',                      mins: 26, apex30: true },
  { name: 'Yuki',    action: 'solved',  item: 'Fluent Query Builder',        mins: 31 },
];

const DIFF_COLORS = {
  Easy:   { bg: '#e6f4ea', color: '#1a6b2e' },
  Medium: { bg: '#fff3e0', color: '#8a4a00' },
  Hard:   { bg: '#fce8e8', color: '#9c1f1f' },
};

const SF_CODEY = 'https://www.salesforce.com/news/wp-content/uploads/sites/3/2022/06/CODEY_VestUkulele_RightFront_SFS20_sRGB.png';
const SF_RUTH  = 'https://www.salesforce.com/news/wp-content/uploads/sites/3/2022/06/RUTH-RegOutfit-HoldingLaptopLeft-SFS22-sRGB.png';

const CODE_CARDS = [
  {
    filename: 'AccountTriggerHandler.cls',
    badge: '⚙️ Apex · Easy',
    badgeColor: '#1a6b2e',
    badgeBg: '#e6f4ea',
    lines: [
      { indent: 0,  parts: [{ t: 'kw', v: 'public class ' }, { t: 'ty', v: 'AccountTriggerHandler ' }, { t: 'tx', v: '{' }] },
      { indent: 1,  parts: [{ t: 'kw', v: 'public static void ' }, { t: 'fn', v: 'setRating' }, { t: 'tx', v: '(List<Account> accs) {' }] },
      { indent: 2,  parts: [{ t: 'kw', v: 'for ' }, { t: 'tx', v: '(Account a : accs) {' }] },
      { indent: 3,  parts: [{ t: 'tx', v: 'a.Rating = ' }, { t: 'st', v: "'Hot'" }, { t: 'tx', v: ';' }] },
      { indent: 2,  parts: [{ t: 'tx', v: '}' }] },
      { indent: 1,  parts: [{ t: 'tx', v: '}' }] },
      { indent: 0,  parts: [{ t: 'tx', v: '}' }] },
    ],
    tests: ['testRatingSetOnInsert', 'testBulkAccounts', 'testNullSafety'],
    summary: 'Validated in your org',
  },
  {
    filename: 'StatusCallout.cls',
    badge: '🔗 Integrations · Medium',
    badgeColor: '#8a4a00',
    badgeBg: '#fff3e0',
    lines: [
      { indent: 0,  parts: [{ t: 'kw', v: 'public class ' }, { t: 'ty', v: 'StatusCallout ' }, { t: 'tx', v: '{' }] },
      { indent: 1,  parts: [{ t: 'kw', v: 'public static ' }, { t: 'ty', v: 'String ' }, { t: 'fn', v: 'getStatus' }, { t: 'tx', v: '() {' }] },
      { indent: 2,  parts: [{ t: 'ty', v: 'HttpRequest ' }, { t: 'tx', v: 'req = ' }, { t: 'kw', v: 'new ' }, { t: 'ty', v: 'HttpRequest' }, { t: 'tx', v: '();' }] },
      { indent: 2,  parts: [{ t: 'tx', v: 'req.setEndpoint(' }, { t: 'st', v: "'https://api.example.com'" }, { t: 'tx', v: ');' }] },
      { indent: 2,  parts: [{ t: 'tx', v: 'req.setMethod(' }, { t: 'st', v: "'GET'" }, { t: 'tx', v: ');' }] },
      { indent: 2,  parts: [{ t: 'kw', v: 'return new ' }, { t: 'ty', v: 'Http' }, { t: 'tx', v: '().send(req).getBody();' }] },
      { indent: 1,  parts: [{ t: 'tx', v: '}' }] },
      { indent: 0,  parts: [{ t: 'tx', v: '}' }] },
    ],
    tests: ['testCalloutSuccess', 'testCalloutFailure'],
    summary: 'Mock response: 200 OK',
  },
  {
    filename: 'AccountBatchJob.cls',
    badge: '⏳ Async Apex · Hard',
    badgeColor: '#9c1f1f',
    badgeBg: '#fce8e8',
    lines: [
      { indent: 0,  parts: [{ t: 'kw', v: 'public class ' }, { t: 'ty', v: 'AccountBatchJob' }] },
      { indent: 1,  parts: [{ t: 'kw', v: 'implements ' }, { t: 'ty', v: 'Database.Batchable' }, { t: 'tx', v: '<SObject> {' }] },
      { indent: 1,  parts: [{ t: 'kw', v: 'public ' }, { t: 'ty', v: 'Database.QueryLocator ' }, { t: 'fn', v: 'start' }, { t: 'tx', v: '(...)  {' }] },
      { indent: 2,  parts: [{ t: 'kw', v: 'return ' }, { t: 'ty', v: 'Database' }, { t: 'tx', v: '.getQueryLocator(' }] },
      { indent: 3,  parts: [{ t: 'st', v: "'SELECT Id FROM Account'" }, { t: 'tx', v: ');' }] },
      { indent: 1,  parts: [{ t: 'tx', v: '}' }] },
      { indent: 0,  parts: [{ t: 'tx', v: '}' }] },
    ],
    tests: ['testBatchExecution', 'testBatchScope', 'testBatchFinish'],
    summary: 'Batch executed · 200 records',
  },
];

function useCounter(target, duration = 1400, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start || target === 0) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function ThemeToggle({ c }) {
  const { theme, toggle } = useTheme();
  const isLight = theme === 'light';
  return (
    <button onClick={toggle} aria-label="Toggle theme" style={{ width: '52px', height: '28px', background: c.card, border: `1px solid ${c.border}`, borderRadius: '14px', cursor: 'pointer', position: 'relative', transition: 'background 0.25s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: '3px', left: isLight ? '25px' : '3px', width: '20px', height: '20px', background: isLight ? '#f59e0b' : c.accent, borderRadius: '50%', transition: 'left 0.25s', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>
        {isLight ? '☀️' : '🌙'}
      </div>
    </button>
  );
}

function CodeLine({ parts, indent }) {
  const ed = { tx: '#9aa1b0', kw: '#c678dd', ty: '#e5c07b', fn: '#61afef', st: '#98c379' };
  return (
    <div style={{ paddingLeft: indent * 16, fontSize: 11.5, lineHeight: 1.85, fontFamily: "'JetBrains Mono','Fira Code',Consolas,monospace" }}>
      {parts.map((p, i) => <span key={i} style={{ color: ed[p.t] }}>{p.v}</span>)}
    </div>
  );
}

function HeroCardStack() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => { setActiveIndex(i => (i + 1) % CODE_CARDS.length); setFading(false); }, 400);
    }, 3800);
    return () => clearInterval(id);
  }, []);
  const ed = { bg: '#0d0f16', border: 'rgba(255,255,255,0.08)', div: 'rgba(255,255,255,0.07)', muted: '#6b7280', ok: '#10b981' };
  const card = CODE_CARDS[activeIndex];
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 480 }}>
      <div style={{ position: 'absolute', top: -16, left: 16, right: -16, background: ed.bg, border: `1px solid ${ed.border}`, borderRadius: 14, height: 320, opacity: 0.35, filter: 'blur(1px)', zIndex: 1 }} />
      <div style={{ position: 'absolute', top: -8, left: 8, right: -8, background: ed.bg, border: `1px solid ${ed.border}`, borderRadius: 14, height: 320, opacity: 0.6, zIndex: 2 }} />
      <div style={{ position: 'relative', zIndex: 3, background: ed.bg, border: `1px solid ${ed.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.5)', transition: 'opacity 0.4s ease', opacity: fading ? 0 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', borderBottom: `1px solid ${ed.div}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {['#f87171','#fbbf24','#10b981'].map(bg => <span key={bg} style={{ width: 8, height: 8, borderRadius: '50%', background: bg }} />)}
            <span style={{ fontSize: 11, color: ed.muted, fontFamily: "'JetBrains Mono',monospace" }}>{card.filename}</span>
          </div>
          <span style={{ fontSize: 10, fontWeight: 600, color: card.badgeColor, background: card.badgeBg, padding: '2px 8px', borderRadius: 4 }}>{card.badge}</span>
        </div>
        <div style={{ padding: '14px 18px', minHeight: 180 }}>
          {card.lines.map((line, i) => <CodeLine key={i} parts={line.parts} indent={line.indent} />)}
        </div>
        <div style={{ borderTop: `1px solid ${ed.div}`, padding: '12px 18px', background: 'rgba(16,185,129,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: ed.ok }}>✓ All tests passed · {card.tests.length}/{card.tests.length}</span>
            <span style={{ fontSize: 11, color: ed.muted }}>{card.summary}</span>
          </div>
          {card.tests.map(t => <div key={t} style={{ fontSize: 11, color: '#9aa1b0', lineHeight: 1.8, fontFamily: "'JetBrains Mono',monospace" }}><span style={{ color: ed.ok }}>✓</span> {t}</div>)}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 20, position: 'relative', zIndex: 4 }}>
        {CODE_CARDS.map((_, i) => <div key={i} style={{ width: i === activeIndex ? 20 : 6, height: 6, borderRadius: 3, background: i === activeIndex ? '#4f8ef7' : 'rgba(255,255,255,0.2)', transition: 'all 0.4s ease' }} />)}
      </div>
      <img src={SF_CODEY} alt="Codey the Salesforce bear" style={{ position: 'absolute', bottom: -10, right: -60, width: 180, height: 'auto', zIndex: 5, filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.4))', pointerEvents: 'none' }} />
    </div>
  );
}

function ActivityTicker({ c }) {
  const tickerRef = useRef(null);
  useEffect(() => {
    let frame, pos = 0;
    const speed = 0.4;
    const step = () => {
      const el = tickerRef.current;
      if (el) {
        pos += speed;
        if (pos > el.scrollWidth / 2) pos = 0;
        el.style.transform = `translateX(-${pos}px)`;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, []);
  const items = [...ACTIVITY_FEED, ...ACTIVITY_FEED];
  return (
    <div style={{ background: c.bgAlt, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`, padding: '10px 0', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      <div style={{ flexShrink: 0, padding: '0 20px', borderRight: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: c.secondary, whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.success, display: 'inline-block', animation: 'tcPulse 2s infinite' }} />
        Live activity
      </div>
      <div style={{ overflow: 'hidden', flex: 1 }}>
        <div ref={tickerRef} style={{ display: 'flex', gap: 32, paddingLeft: 24, willChange: 'transform' }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap', color: c.secondary }}>
              <span style={{ color: c.accent, fontWeight: 600 }}>{item.name}</span>
              <span>{item.action}</span>
              <span style={{ color: item.apex30 ? c.accent : c.text, fontWeight: item.apex30 ? 600 : 400 }}>{item.item}</span>
              <span style={{ color: c.muted, fontSize: 11 }}>· {item.mins}m ago</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Apex30Banner({ c, navigate }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0 });
  const now = new Date();
  const year = now.getFullYear();
  const julyStart = new Date(year, 6, 1);
  const julyEnd   = new Date(year, 6, 30, 23, 59, 59);
  const isBeforeJuly = now < julyStart;
  const isJuly       = now >= julyStart && now <= julyEnd;
  const isAfterJuly  = now > julyEnd;
  const julyDay = isJuly ? Math.min(30, Math.floor((now - julyStart) / 86400000) + 1) : isAfterJuly ? 30 : 0;
  const progressPct = Math.round((julyDay / 30) * 100);
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      const target = n < julyStart ? julyStart : julyEnd;
      const diff = Math.max(0, target - n);
      setTimeLeft({ days: Math.floor(diff / 86400000), hours: Math.floor((diff % 86400000) / 3600000), mins: Math.floor((diff % 3600000) / 60000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ background: '#08111f', borderTop: '1px solid #1c2d45', borderBottom: '1px solid #1c2d45', padding: '36px clamp(24px, 6vw, 120px)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr auto', gap: 40, alignItems: 'center' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(79,142,247,0.12)', border: '1px solid rgba(79,142,247,0.25)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: '#4f8ef7', marginBottom: 12 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4f8ef7', animation: 'tcPulse 2s infinite' }} />
            July challenge · New
          </div>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: '#ffffff', margin: '0 0 8px', letterSpacing: '-0.5px' }}>Apex<span style={{ color: '#4f8ef7' }}>30</span> — 30 days. 30 challenges.</h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: '0 0 20px', maxWidth: 440 }}>One Salesforce challenge per day in July. Build the habit. Join 47 developers already on the streak.</p>
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>
              {isBeforeJuly ? <span>Starts July 1</span> : isAfterJuly ? <span>Challenge complete</span> : <span>Day {julyDay} of 30</span>}
              <span>{progressPct}% of July complete</span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
              <div style={{ height: 4, background: '#4f8ef7', borderRadius: 2, width: `${progressPct}%`, transition: 'width 1s ease' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 480 }}>
            {Array.from({ length: 30 }, (_, i) => {
              const day = i + 1;
              const done = isJuly && day < julyDay;
              const isToday = isJuly && day === julyDay;
              const past = isAfterJuly;
              return <div key={day} style={{ width: 22, height: 22, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontFamily: 'monospace', fontWeight: 600, background: past || done ? '#1a3a6e' : isToday ? '#4f8ef7' : 'rgba(255,255,255,0.05)', color: past || done ? '#7ab4f5' : isToday ? '#fff' : 'rgba(255,255,255,0.2)', border: isToday ? '1px solid #4f8ef7' : '1px solid transparent' }}>{past || done ? '✓' : day}</div>;
            })}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {!isAfterJuly && (
            <>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{isBeforeJuly ? 'Starts in' : 'Time remaining'}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ val: String(timeLeft.days).padStart(2,'0'), label: 'days' }, { val: String(timeLeft.hours).padStart(2,'0'), label: 'hours' }, { val: String(timeLeft.mins).padStart(2,'0'), label: 'mins' }].map(({ val, label }) => (
                  <div key={label} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px 18px', textAlign: 'center', minWidth: 64 }}>
                    <span style={{ display: 'block', fontSize: 24, fontWeight: 700, color: '#fff', fontFamily: 'monospace', lineHeight: 1 }}>{val}</span>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 4, display: 'block' }}>{label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <button onClick={() => navigate('/apex30')} style={{ background: '#4f8ef7', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap', boxShadow: '0 0 24px rgba(79,142,247,0.3)', transition: 'opacity 0.15s' }} onMouseEnter={e => e.target.style.opacity = '0.85'} onMouseLeave={e => e.target.style.opacity = '1'}>Join Apex30 →</button>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>Free · No credit card</span>
        </div>
      </div>
    </div>
  );
}

function ScrollReveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
}

function Home() {
  const { openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const [phraseIndex,     setPhraseIndex]     = useState(0);
  const [phraseVisible,   setPhraseVisible]   = useState(true);
  const [animateStats,    setAnimateStats]    = useState(false);
  const [stats,           setStats]           = useState({ challenges: 0, topics: 0 });
  const [statsLoading,    setStatsLoading]    = useState(true);
  const statsRef = useRef(null);

  // ── Redirect logged-in users to dashboard ──────────────────────────────────
  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/challenges`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setStats({ challenges: data.length, topics: new Set(data.map(c => c.topic)).size });
      })
      .catch(() => setStats({ challenges: 149, topics: 12 }))
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => { setPhraseIndex(i => (i + 1) % ROTATING_PHRASES.length); setPhraseVisible(true); }, 350);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setAnimateStats(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);



  
  const challengeCount = useCounter(stats.challenges, 1400, animateStats && !statsLoading);
  const topicCount     = useCounter(stats.topics, 1200, animateStats && !statsLoading);

  // Don't render marketing page for logged-in users (they get redirected)
  if (user) return null;

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s' }}>
      <SEO />

      {/* ── Nav ── */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 clamp(24px, 6vw, 120px)', height: 58, background: c.navBg, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.border}`, position: 'sticky', top: 0, zIndex: 100, transition: 'background 0.25s' }}>
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px' }}>Trail<span style={{ color: c.accent }}>Code</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {[['Challenges','#challenges'],['Interview Prep','/interview'],['👥 Teams','/team'],['Pricing','/pricing']].map(([label, href]) => (
            <a key={label} href={href} style={{ color: c.secondary, textDecoration: 'none', fontSize: 13, padding: '5px 10px', borderRadius: 6, transition: 'color 0.15s, background 0.15s' }}
              onMouseEnter={e => { e.target.style.color = c.text; e.target.style.background = c.card; }}
              onMouseLeave={e => { e.target.style.color = c.secondary; e.target.style.background = 'transparent'; }}>
              {label}
            </a>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ThemeToggle c={c} />
          <button onClick={() => openAuthModal()} style={{ background: c.accent, color: '#fff', border: 'none', padding: '7px 18px', fontSize: 13, borderRadius: 7, cursor: 'pointer', fontWeight: 600, fontFamily: "'Inter', sans-serif", transition: 'opacity 0.15s' }}
            onMouseEnter={e => e.target.style.opacity = '0.85'}
            onMouseLeave={e => e.target.style.opacity = '1'}>
            Login
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <div style={{ padding: '72px clamp(24px, 6vw, 120px) 100px', maxWidth: 1400, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 64, alignItems: 'center' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: c.accentBg, border: `1px solid ${c.accentBorder}`, borderRadius: 20, padding: '5px 14px', fontSize: 12, color: c.accent, marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.accent, display: 'inline-block', animation: 'tcPulse 2s infinite' }} />
            Built by a developer who needed this
          </div>
          <h1 style={{ fontSize: 'clamp(34px, 4.5vw, 52px)', fontWeight: 800, lineHeight: 1.1, color: c.text, margin: '0 0 16px', letterSpacing: '-2px' }}>
            The platform to<br />
            <span style={{ color: c.accent, display: 'inline-block', transition: 'opacity 0.35s ease, transform 0.35s ease', opacity: phraseVisible ? 1 : 0, transform: phraseVisible ? 'translateY(0)' : 'translateY(8px)', minHeight: '1.2em' }}>
              {ROTATING_PHRASES[phraseIndex]}
            </span>
          </h1>
          <p style={{ fontSize: 15, color: c.secondary, maxWidth: 420, margin: '0 0 20px', lineHeight: 1.75 }}>
            Real challenges. Real org validation. Concepts that explain the why. One platform for every Salesforce skill.
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: c.card, border: `1px solid ${c.border}`, borderRadius: 20, padding: '5px 14px', fontSize: 12, color: c.secondary, marginBottom: 28 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.success, display: 'inline-block' }} />
            {statsLoading ? 'Loading...' : <><strong style={{ color: c.text }}>{stats.challenges}</strong>&nbsp;challenges across&nbsp;<strong style={{ color: c.text }}>{stats.topics}</strong>&nbsp;topics — updated live</>}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => openAuthModal()} style={{ background: c.accent, color: '#fff', border: 'none', padding: '13px 28px', fontSize: 15, borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontFamily: "'Inter', sans-serif", transition: 'opacity 0.15s, transform 0.12s', boxShadow: theme === 'dark' ? '0 0 24px rgba(79,142,247,0.25)' : '0 2px 12px rgba(45,111,240,0.3)' }}
              onMouseEnter={e => { e.target.style.opacity = '0.88'; e.target.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0)'; }}>
              Start for free →
            </button>
            <button onClick={() => navigate('/apex30')} style={{ background: '#08111f', color: '#4f8ef7', border: '1.5px solid #4f8ef7', padding: '13px 24px', fontSize: 14, borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontFamily: "'Inter', sans-serif", transition: 'all 0.15s' }}
              onMouseEnter={e => { e.target.style.background = '#4f8ef7'; e.target.style.color = '#fff'; }}
              onMouseLeave={e => { e.target.style.background = '#08111f'; e.target.style.color = '#4f8ef7'; }}>
              🔥 Join Apex30
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', paddingRight: 60 }}>
          <HeroCardStack />
        </div>
      </div>

      {/* ── Activity Ticker ── */}
      <ActivityTicker c={c} />

      {/* ── Stats ── */}
      <div ref={statsRef} style={{ borderBottom: `1px solid ${c.border}`, padding: '44px clamp(24px, 6vw, 120px)', background: c.bg }}>
        <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {[
            { val: statsLoading ? '—' : challengeCount || stats.challenges, label: 'Challenges', suffix: '' },
            null,
            { val: statsLoading ? '—' : topicCount || stats.topics, label: 'Topics', suffix: '' },
            null,
            { val: '100', label: 'Validations run', suffix: '+' },
          ].map((s, i) => s === null ? (
            <div key={i} style={{ width: 1, height: 40, background: c.border }} />
          ) : (
            <div key={i} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 40, fontWeight: 800, color: c.accent, margin: '0 0 4px', letterSpacing: '-2px' }}>{s.val}{s.suffix}</p>
              <p style={{ fontSize: 11, color: c.secondary, margin: 0, textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 500 }}>{s.label}</p>
            </div>
          ))}
        </div>
        {!statsLoading && <p style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: c.muted }}>Live from the database · Last fetched just now</p>}
      </div>

      {/* ── Apex30 Banner ── */}
      <Apex30Banner c={c} navigate={navigate} />

      {/* ── Why TrailCode ── */}
      <div id="why" style={{ padding: '88px clamp(24px, 6vw, 120px)', maxWidth: 1400, margin: '0 auto' }}>
        <ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.6fr)', gap: 64, alignItems: 'center' }}>
            <div>
              <p style={{ color: c.accent, fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 }}>Why TrailCode</p>
              <h2 style={{ fontSize: 30, fontWeight: 700, marginBottom: 16, color: c.text, letterSpacing: '-0.5px', lineHeight: 1.3 }}>Not another documentation page</h2>
              <p style={{ fontSize: 14, color: c.secondary, lineHeight: 1.8, marginBottom: 28 }}>
                When I started learning Salesforce development I couldn't find a single place that let me actually practice and get instant feedback. So I built it.
              </p>
              <button onClick={() => openAuthModal()} style={{ background: 'transparent', border: `1.5px solid ${c.accent}`, color: c.accent, padding: '10px 22px', borderRadius: 7, cursor: 'pointer', fontSize: 14, fontFamily: "'Inter', sans-serif", fontWeight: 600, transition: 'all 0.15s' }}
                onMouseEnter={e => { e.target.style.background = c.accent; e.target.style.color = theme === 'dark' ? '#08090e' : '#fff'; }}
                onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = c.accent; }}>
                Get started free →
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: c.shadow }}>
                {FEATURES.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '18px 24px', borderBottom: i < FEATURES.length - 1 ? `1px solid ${c.border}` : 'none', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = c.cardHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: c.accentBg, border: `1px solid ${c.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <span style={{ color: c.accent, fontSize: 11, fontWeight: 700 }}>✓</span>
                    </div>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: c.text, margin: '0 0 3px' }}>{f.title}</p>
                      <p style={{ fontSize: 12, color: c.secondary, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img src={SF_RUTH} alt="Ruth the Salesforce architect" style={{ position: 'absolute', bottom: -40, right: -50, width: 160, height: 'auto', filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.3))', pointerEvents: 'none', zIndex: 2 }} />
            </div>
          </div>
        </ScrollReveal>
      </div>

      <div style={{ borderTop: `1px solid ${c.border}`, maxWidth: 960, margin: '0 auto' }} />

      {/* ── Categories ── */}
      <div id="challenges" style={{ padding: '88px clamp(24px, 6vw, 120px)', maxWidth: 1400, margin: '0 auto', background: c.bgAlt }}>
        <ScrollReveal>
          <p style={{ color: c.accent, fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 }}>Practice by category</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 36, flexWrap: 'wrap', gap: 12 }}>
            <h2 style={{ fontSize: 30, fontWeight: 700, color: c.text, letterSpacing: '-0.3px', margin: 0 }}>Pick where you want to grow</h2>
            {!statsLoading && <span style={{ fontSize: 13, color: c.secondary }}><strong style={{ color: c.accent }}>{stats.challenges}</strong> challenges ready</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {CATEGORIES.map((cat, idx) => (
              <ScrollReveal key={cat.id} delay={idx * 60}>
                <div onClick={() => openAuthModal()} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: '22px 18px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: c.shadow, height: '100%' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.background = c.cardHover; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.background = c.card; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  <div style={{ fontSize: 26, marginBottom: 12 }}>{cat.icon}</div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: c.text }}>{cat.label}</h3>
                  <p style={{ fontSize: 12, color: c.secondary, lineHeight: 1.6, margin: '0 0 12px' }}>{cat.desc}</p>
                  <div style={{ display: 'flex', gap: 5, marginBottom: 14, flexWrap: 'wrap' }}>
                    {cat.diffs.map(d => <span key={d} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: DIFF_COLORS[d].bg, color: DIFF_COLORS[d].color, fontWeight: 600 }}>{d}</span>)}
                  </div>
                  <span style={{ fontSize: 13, color: c.accent, fontWeight: 600 }}>Explore →</span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </ScrollReveal>
      </div>

      {/* ── How it works ── */}
      <div style={{ padding: '88px clamp(24px, 6vw, 120px)', background: c.bg }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <ScrollReveal>
            <p style={{ color: c.accent, fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 }}>How it works</p>
            <h2 style={{ fontSize: 30, fontWeight: 700, marginBottom: 52, color: c.text, letterSpacing: '-0.3px' }}>From zero to validated in minutes</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40 }}>
              {HOW_IT_WORKS.map((item, i) => (
                <ScrollReveal key={i} delay={i * 80}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: c.accentBg, border: `1px solid ${c.accentBorder}`, fontSize: 12, fontWeight: 800, color: c.accent, marginBottom: 14 }}>{item.step}</div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: c.text, marginBottom: 8 }}>{item.title}</h3>
                  <p style={{ fontSize: 13, color: c.secondary, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </ScrollReveal>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer style={{ borderTop: `1px solid ${c.border}`, padding: '28px clamp(24px, 6vw, 120px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
        <span style={{ fontWeight: 800, color: c.text, letterSpacing: '-0.3px' }}>Trail<span style={{ color: c.accent }}>Code</span></span>
        <div style={{ display: 'flex', gap: 24 }}>
          {[['Pricing','/pricing'],['FAQ','/faq'],['Contact','/contact']].map(([label, path]) => (
            <span key={label} onClick={() => navigate(path)} style={{ color: c.secondary, cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={e => e.target.style.color = c.text}
              onMouseLeave={e => e.target.style.color = c.secondary}>
              {label}
            </span>
          ))}
        </div>
        <span style={{ color: c.muted, fontSize: 12 }}>Built by a developer who couldn't find this platform</span>
      </footer>

      <style>{`
        @keyframes tcPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.75)} }
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
      `}</style>
    </div>
  );
}

export default Home;